import { useStages } from "@/context/StagesContext";
import { useUser } from "@/context/UserContext";
import { getFormattedDate, isValidDate } from "@/helpers/date.helper";
import { getOccupationZones } from "@/services/occupations";
import { Occupation } from "@/services/occupations/types/occupations.types";
import { updateUserInfo } from "@/services/users";
import { fetchAddressByCep } from "@/services/viacep/fetchAddressByCep";
import { getRegion } from "@/utils/get-localities";
import { getOnlyDigits, isValidCpf } from "@/utils/cpf";
import {
  PROFESSIONAL_OF_WORK_ZONE_ID,
  PROFESSIONAL_OF_WORK_ZONE_LABEL,
} from "@/utils/profession.constants";
import {
  fullNameRegex,
  fullPhoneNumberRegex,
  phoneNumberRegex,
} from "@/utils/regex";
import { isAfter } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  isPostAdvanceProfileCompleteFromForm,
  markPostAdvanceRegionalBriefingPending,
} from "@/utils/postAdvanceRegionalBriefing";

export const useProfile = () => {
  const { userInfo, refetchUserInfo } = useUser();
  const { currentStageNumber, isValidStage } = useStages();

  const [imageUrl, setImageUrl] = useState(userInfo?.imageUrl);
  const [name, setName] = useState(userInfo?.name);
  const [email, setEmail] = useState(userInfo?.email);
  const [phoneNumber, setPhoneNumber] = useState(
    userInfo?.phoneNumber?.slice(3) || ""
  );
  const [birthDate, setBirthDate] = useState(
    getFormattedDate(userInfo?.birthDate, true)
  );
  const [state, setState] = useState(userInfo?.state);
  const [region, setRegion] = useState("");
  const [city, setCity] = useState(userInfo?.city);
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [selectedProfessionName, setSelectedProfessionName] = useState(
    userInfo?.profession ?? ""
  );
  const [timeOfProfession, setTimeOfProfession] = useState(
    userInfo?.timeOfProfession ?? ""
  );
  const [instagramAccount, setInstagramAccount] = useState(
    userInfo?.instagramAccount
  );
  const [gender, setGender] = useState(userInfo?.gender);
  const [address, setAddress] = useState(userInfo?.address);
  const [numberAddress, setNumberAddress] = useState(userInfo?.numberAddress);
  const [complement, setComplement] = useState(userInfo?.complement);
  const [neighborhood, setNeighborhood] = useState(userInfo?.neighborhood);
  const [cep, setCep] = useState(userInfo?.cep);
  const [shirtSize, setShirtSize] = useState(userInfo?.shirtSize);

  const [cpf, setCpf] = useState(userInfo?.cpf);
  const [phoneNumberResponsible, setPhoneNumberResponsible] = useState(
    userInfo?.phoneNumberResponsible
  );
  const [shoeSize, setShoeSize] = useState(userInfo?.shoeSize);
  const [pantsSize, setPantsSize] = useState(userInfo?.pantsSize);
  const [gloveSize, setGloveSize] = useState(userInfo?.gloveSize);
  const [addressProofUrl, setAddressProofUrl] = useState(
    userInfo?.addressProofUrl
  );
  const [identityDocumentUrl, setIdentityDocumentUrl] = useState(
    userInfo?.identityDocumentUrl
  );
  const [fatherName, setFatherName] = useState(userInfo?.fatherName);
  const [motherName, setMotherName] = useState(userInfo?.motherName);
  const [birthPlace, setBirthPlace] = useState(userInfo?.birthPlace);
  const [maritalStatus, setMaritalStatus] = useState(userInfo?.maritalStatus);
  const [rgRne, setRgRne] = useState(userInfo?.rgRne);
  const [rgIssuingAgency, setRgIssuingAgency] = useState(
    userInfo?.rgIssuingAgency
  );
  const [rgIssueDate, setRgIssueDate] = useState(
    getFormattedDate(userInfo?.rgIssueDate, true)
  );

  const [isLoading, setIsLoading] = useState(false);
  const [cepLookupLoading, setCepLookupLoading] = useState(false);
  const [cepLookupHint, setCepLookupHint] = useState("");

  const [showChangesDialog, setShowChangesDialog] = useState(false);
  const toggleChangesDialog = () => setShowChangesDialog(!showChangesDialog);

  const [showConfirmChangesDialog, setShowConfirmChangesDialog] =
    useState(false);
  const toggleConfirmDialog = () =>
    setShowConfirmChangesDialog(!showConfirmChangesDialog);

  const [showChangesSavedDialog, setShowChangesSavedDialog] = useState(false);
  const toggleChangesSavedDialog = () =>
    setShowChangesSavedDialog(!showChangesSavedDialog);

  const [isOpenInstagramTooltip, setIsOpenInstagramTooltip] = useState(false);
  const toggleTooltip = () =>
    setIsOpenInstagramTooltip(!isOpenInstagramTooltip);

  const rawPhoneNumber = useMemo(() => {
    if (!phoneNumber) return "";

    return "+55" + phoneNumber.replace(/[^\d]/g, "");
  }, [phoneNumber]);

  const filteredOccupations = useMemo(() => {
    return occupations.filter((occupation) =>
      occupation.zoneId.includes(PROFESSIONAL_OF_WORK_ZONE_ID)
    );
  }, [occupations]);

  /** Ensures MUI Select value always matches an option (fixes warning while occupations load). */
  const professionSelectOptions = useMemo(() => {
    const list = filteredOccupations;
    const name = selectedProfessionName?.trim();
    if (!name) return list;
    if (list.some((o) => o.name === name)) return list;
    return [
      {
        id: -1,
        name,
        zoneId: [PROFESSIONAL_OF_WORK_ZONE_ID],
      },
      ...list,
    ];
  }, [filteredOccupations, selectedProfessionName]);

  const hasChanges = useMemo(() => {
    let isDifferent =
      imageUrl !== userInfo?.imageUrl ||
      name !== userInfo?.name ||
      email !== userInfo?.email ||
      rawPhoneNumber !== userInfo?.phoneNumber ||
      birthDate !== getFormattedDate(userInfo?.birthDate, true) ||
      state !== userInfo?.state ||
      city !== userInfo?.city ||
      selectedProfessionName !== (userInfo?.profession ?? "") ||
      timeOfProfession !== (userInfo?.timeOfProfession ?? "") ||
      instagramAccount !== userInfo?.instagramAccount ||
      gender !== userInfo?.gender;

    if (currentStageNumber > 1) {
      isDifferent =
        isDifferent ||
        address !== userInfo?.address ||
        numberAddress !== userInfo?.numberAddress ||
        complement !== userInfo?.complement ||
        neighborhood !== userInfo?.neighborhood ||
        cep !== userInfo?.cep ||
        shirtSize !== userInfo?.shirtSize ||
        shoeSize !== userInfo?.shoeSize ||
        pantsSize !== userInfo?.pantsSize ||
        gloveSize !== userInfo?.gloveSize ||
        addressProofUrl !== userInfo?.addressProofUrl ||
        identityDocumentUrl !== userInfo?.identityDocumentUrl ||
        fatherName !== userInfo?.fatherName ||
        motherName !== userInfo?.motherName ||
        birthPlace !== userInfo?.birthPlace ||
        maritalStatus !== userInfo?.maritalStatus ||
        rgRne !== userInfo?.rgRne ||
        rgIssuingAgency !== userInfo?.rgIssuingAgency ||
        rgIssueDate !== getFormattedDate(userInfo?.rgIssueDate, true);
    }
    if (currentStageNumber > 2) {
      isDifferent =
        isDifferent ||
        cpf !== userInfo?.cpf ||
        phoneNumberResponsible !== userInfo?.phoneNumberResponsible;
    }

    return isDifferent;
  }, [
    imageUrl,
    name,
    email,
    phoneNumber,
    birthDate,
    state,
    city,
    instagramAccount,
    gender,
    address,
    numberAddress,
    complement,
    neighborhood,
    cep,
    cpf,
    phoneNumberResponsible,
    shoeSize,
    pantsSize,
    gloveSize,
    addressProofUrl,
    identityDocumentUrl,
    fatherName,
    motherName,
    birthPlace,
    maritalStatus,
    rgRne,
    rgIssuingAgency,
    rgIssueDate,
    shirtSize,
    currentStageNumber,
    userInfo,
    rawPhoneNumber,
    selectedProfessionName,
    timeOfProfession,
  ]);

  const rawPhoneNumberContactResponse = useMemo(
    () => phoneNumberResponsible?.replace(/[^\d]/g, "") ?? "",
    [phoneNumberResponsible]
  );

  const phoneNumberError = useMemo(() => {
    if (rawPhoneNumber?.length === 0) return "";

    const isValid = fullPhoneNumberRegex.test(rawPhoneNumber);

    if (!isValid) {
      return "Número inválido.";
    }

    return "";
  }, [rawPhoneNumber]);

  const nameError = useMemo(() => {
    if (!name) return "";

    const isValid = fullNameRegex.test(name);

    if (!isValid) {
      return "Nome incompleto.";
    }

    return "";
  }, [name]);

  const phoneNumberResponsibleError = useMemo(() => {
    if (rawPhoneNumberContactResponse?.length === 0) return "";

    const isValid = phoneNumberRegex.test(rawPhoneNumberContactResponse);

    if (!isValid) {
      return "Número inválido.";
    }

    return "";
  }, [rawPhoneNumberContactResponse]);

  const cpfError = useMemo(() => {
    const cpfDigits = getOnlyDigits(cpf ?? "");

    if (cpfDigits.length === 0) return "Campo obrigatório";
    if (!isValidCpf(cpfDigits)) return "CPF inválido.";

    return "";
  }, [cpf]);

  const rawBirthdate = useMemo(() => {
    if (!birthDate) return "";

    const [day, month, year] = birthDate.split("/");

    return `${year}-${month}-${day}`;
  }, [birthDate]);

  const birthDateError = useMemo(() => {
    if (rawBirthdate?.length === 0) return "";

    const year = rawBirthdate.split("-").shift();
    if (
      !isValidDate(rawBirthdate) ||
      isAfter(rawBirthdate, new Date()) ||
      Number(year) < 1901
    ) {
      return "Data inválida.";
    }

    return "";
  }, [rawBirthdate]);

  const rawRgIssueDate = useMemo(() => {
    if (!rgIssueDate) return "";

    const [day, month, year] = rgIssueDate.split("/");

    return `${year}-${month}-${day}`;
  }, [rgIssueDate]);

  const rgIssueDateError = useMemo(() => {
    if (rawRgIssueDate?.length === 0) return "";

    const year = rawRgIssueDate.split("-").shift();
    if (
      !isValidDate(rawRgIssueDate) ||
      isAfter(rawRgIssueDate, new Date()) ||
      Number(year) < 1901
    ) {
      return "Data inválida.";
    }

    return "";
  }, [rawRgIssueDate]);

  const isValid = useMemo(() => {
    let hasAllFields =
      !!name &&
      !nameError &&
      !!email &&
      !!phoneNumber &&
      !!birthDate &&
      !!state &&
      !!city &&
      !!gender &&
      !!timeOfProfession &&
      !birthDateError &&
      !phoneNumberError &&
      !cpfError;

    if (currentStageNumber > 1) {
      hasAllFields =
        hasAllFields &&
        !!address &&
        !!numberAddress &&
        !!neighborhood &&
        !!cep &&
        !!shirtSize &&
        !!shoeSize &&
        !!pantsSize &&
        !!gloveSize &&
        !!addressProofUrl &&
        !!identityDocumentUrl &&
        !!motherName &&
        !!birthPlace &&
        !!maritalStatus &&
        !!rgRne &&
        !!rgIssuingAgency &&
        !!rgIssueDate &&
        !rgIssueDateError;
    }

    return hasAllFields;
  }, [
    name,
    nameError,
    email,
    phoneNumber,
    birthDate,
    state,
    city,
    instagramAccount,
    gender,
    timeOfProfession,
    birthDateError,
    phoneNumberError,
    address,
    numberAddress,
    complement,
    neighborhood,
    cep,
    shirtSize,
    shoeSize,
    pantsSize,
    gloveSize,
    addressProofUrl,
    identityDocumentUrl,
    fatherName,
    motherName,
    birthPlace,
    maritalStatus,
    rgRne,
    rgIssuingAgency,
    rgIssueDate,
    rgIssueDateError,
    currentStageNumber,
    cpfError,
  ]);

  const onPressCancel = () => {
    if (hasChanges) {
      return toggleChangesDialog();
    }
  };

  const onPressSave = () => {
    setShowConfirmChangesDialog(true);
  };

  const onCancelChanges = () => {
    resetInfo();

    toggleChangesDialog();
  };

  const onSaveChanges = async () => {
    try {
      setIsLoading(true);

      if (!isValid) {
        toggleConfirmDialog();
        return toast("Preencha todos os campos corretamente.", {
          type: "error",
        });
      }

      await updateUserInfo({
        imageUrl,
        name,
        email,
        birthDate: rawBirthdate,
        state,
        city,
        occupationField: PROFESSIONAL_OF_WORK_ZONE_LABEL,
        profession: selectedProfessionName,
        timeOfProfession,
        instagramAccount,
        gender,
        address,
        numberAddress,
        complement,
        neighborhood,
        cep,
        shirtSize,
        cpf: getOnlyDigits(cpf ?? ""),
        phoneNumberResponsible: phoneNumberResponsible?.replace(/[() -]/g, ""),
        shoeSize,
        pantsSize,
        gloveSize,
        addressProofUrl,
        identityDocumentUrl,
        fatherName,
        motherName,
        birthPlace,
        maritalStatus,
        rgRne,
        rgIssuingAgency,
        rgIssueDate: rawRgIssueDate,
      });

      const opensRegionalBriefing = isPostAdvanceProfileCompleteFromForm(
        currentStageNumber,
        {
          shoeSize,
          shirtSize,
          pantsSize,
          gloveSize,
          address,
          numberAddress,
          neighborhood,
          complement,
          cep,
          addressProofUrl,
          identityDocumentUrl,
          instagramAccount,
          motherName,
          birthPlace,
          maritalStatus,
          rgRne,
          rgIssuingAgency,
          rgIssueDate: rawRgIssueDate,
        }
      );

      if (opensRegionalBriefing) {
        markPostAdvanceRegionalBriefingPending();
      }

      await refetchUserInfo();

      toggleConfirmDialog();
      if (!opensRegionalBriefing) {
        toggleChangesSavedDialog();
      }
    } catch (err) {
      console.error("Error updating profile info", err);
      toast("Erro salvando informações", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const resetInfo = () => {
    setImageUrl(userInfo?.imageUrl);
    setName(userInfo?.name);
    setEmail(userInfo?.email);
    setPhoneNumber(userInfo?.phoneNumber?.slice(3) || "");
    setBirthDate(getFormattedDate(userInfo?.birthDate, true));
    setState(userInfo?.state);
    setCity(userInfo?.city);
    setSelectedProfessionName(userInfo?.profession ?? "");
    setTimeOfProfession(userInfo?.timeOfProfession ?? "");
    setInstagramAccount(userInfo?.instagramAccount);
    setGender(userInfo?.gender);
    setAddress(userInfo?.address);
    setNumberAddress(userInfo?.numberAddress);
    setComplement(userInfo?.complement);
    setNeighborhood(userInfo?.neighborhood);
    setCep(userInfo?.cep);
    setShirtSize(userInfo?.shirtSize);
    setCpf(userInfo?.cpf);
    setPhoneNumberResponsible(userInfo?.phoneNumberResponsible);
    setShoeSize(userInfo?.shoeSize);
    setPantsSize(userInfo?.pantsSize);
    setGloveSize(userInfo?.gloveSize);
    setAddressProofUrl(userInfo?.addressProofUrl);
    setIdentityDocumentUrl(userInfo?.identityDocumentUrl);
    setFatherName(userInfo?.fatherName);
    setMotherName(userInfo?.motherName);
    setBirthPlace(userInfo?.birthPlace);
    setMaritalStatus(userInfo?.maritalStatus);
    setRgRne(userInfo?.rgRne);
    setRgIssuingAgency(userInfo?.rgIssuingAgency);
    setRgIssueDate(getFormattedDate(userInfo?.rgIssueDate, true));
  };

  const updateRegion = async () => {
    if (!state) {
      setRegion("");
      return;
    }

    const res = await getRegion(state);
    setRegion(res);
  };

  useEffect(() => {
    updateRegion();
  }, [state]);

  useEffect(() => {
    updateRegion();
  }, []);

  useEffect(() => {
    const loadOccupationData = async () => {
      try {
        const data = await getOccupationZones();
        setOccupations(data.occupation ?? []);
      } catch (error) {
        console.error("Error loading occupation data", error);
      }
    };

    loadOccupationData();
  }, []);

  useEffect(() => {
    if (filteredOccupations.length === 0) return;
    const stillValid = filteredOccupations.some(
      (occupation) => occupation.name === selectedProfessionName
    );
    if (!stillValid) setSelectedProfessionName("");
  }, [filteredOccupations, selectedProfessionName]);

  useEffect(() => {
    resetInfo();
  }, [userInfo]);

  useEffect(() => {
    if (currentStageNumber <= 1) {
      setCepLookupHint("");
      setCepLookupLoading(false);
      return;
    }
    if (userInfo?.cep) {
      setCepLookupHint("");
      setCepLookupLoading(false);
      return;
    }

    const digits = (cep ?? "").replace(/\D/g, "");
    if (digits.length !== 8) {
      setCepLookupHint("");
      setCepLookupLoading(false);
      return;
    }

    const canFillAddress = !userInfo?.address;
    const canFillNeighborhood = !userInfo?.neighborhood;
    const canFillComplement = !userInfo?.complement;

    let cancelled = false;
    const t = setTimeout(async () => {
      setCepLookupLoading(true);
      setCepLookupHint("");
      try {
        const data = await fetchAddressByCep(digits);
        if (cancelled) return;
        if (!data) {
          setCepLookupHint("CEP não encontrado.");
          return;
        }
        if (canFillAddress && data.logradouro) {
          setAddress(data.logradouro.trim());
        }
        if (canFillNeighborhood && data.bairro) {
          setNeighborhood(data.bairro.trim());
        }
        if (canFillComplement && data.complemento) {
          setComplement(data.complemento.trim());
        }
      } catch {
        if (!cancelled) {
          setCepLookupHint("Erro ao buscar CEP. Tente novamente.");
        }
      } finally {
        if (!cancelled) {
          setCepLookupLoading(false);
        }
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    cep,
    currentStageNumber,
    userInfo?.cep,
    userInfo?.address,
    userInfo?.neighborhood,
    userInfo?.complement,
  ]);

  return {
    userInfo,
    currentStageNumber,
    isValidStage,
    imageUrl,
    setImageUrl,
    name,
    setName,
    nameError,
    email,
    setEmail,
    phoneNumber,
    setPhoneNumber,
    phoneNumberError,
    birthDate,
    setBirthDate,
    birthDateError,
    state,
    setState,
    city,
    setCity,
    filteredOccupations,
    professionSelectOptions,
    selectedProfessionName,
    setSelectedProfessionName,
    timeOfProfession,
    setTimeOfProfession,
    region,
    setRegion,
    instagramAccount,
    setInstagramAccount,
    gender,
    setGender,
    address,
    setAddress,
    numberAddress,
    setNumberAddress,
    complement,
    setComplement,
    neighborhood,
    setNeighborhood,
    cep,
    setCep,
    cepLookupLoading,
    cepLookupHint,
    shirtSize,
    setShirtSize,
    cpf,
    setCpf,
    phoneNumberResponsible,
    setPhoneNumberResponsible,
    phoneNumberResponsibleError,
    cpfError,
    shoeSize,
    setShoeSize,
    pantsSize,
    setPantsSize,
    gloveSize,
    setGloveSize,
    addressProofUrl,
    setAddressProofUrl,
    identityDocumentUrl,
    setIdentityDocumentUrl,
    fatherName,
    setFatherName,
    motherName,
    setMotherName,
    birthPlace,
    setBirthPlace,
    maritalStatus,
    setMaritalStatus,
    rgRne,
    setRgRne,
    rgIssuingAgency,
    setRgIssuingAgency,
    rgIssueDate,
    setRgIssueDate,
    rgIssueDateError,
    isLoading,
    hasChanges,
    onPressCancel,
    onPressSave,
    showChangesDialog,
    toggleChangesDialog,
    showConfirmChangesDialog,
    toggleConfirmDialog,
    showChangesSavedDialog,
    toggleChangesSavedDialog,
    onCancelChanges,
    onSaveChanges,

    isOpenInstagramTooltip,
    toggleTooltip,
  };
};
